import {openDropdown, Tooltip, Checkbox} from 'platform/components';
import {
  AdaptiveImage,
  Box,
  Center,
  Clickable,
  Hide,
  Icon,
  Image,
  makeStorageUrl,
  Show,
  Spinner,
} from 'platform/foundation';

import {CSSProperties, ReactNode, useEffect} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isTrue} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {useAuditImageSelectionContext} from '../hooks/useAuditImageSelectionContext';

interface AuditImageProps extends TestIdProps {
  url: string;
  ratio: CSSProperties['aspectRatio'];
  imageId?: string;
  contextMenuContent?: ReactNode;
  isImageRotationInProgress?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
  onOpenPreview: () => void;
}

export function AuditImage(props: AuditImageProps) {
  const [isHovered, handleHover, handleLeave] = useBoolean();
  const [isFocused, handleFocus, handleBlur] = useBoolean();
  const isLocalPreview = isTrue(props.url?.startsWith('blob'));
  const {
    isSelectionMode,
    isImageSelected,
    toggleImageSelection,
    registerSelectableImage,
    unregisterSelectableImage,
  } = useAuditImageSelectionContext();

  const {imageId} = props;
  const isSelectable = isSelectionMode && isNotNil(imageId) && !props.isImageRotationInProgress;
  const isSelected = isSelectable && isNotNil(imageId) && isImageSelected(imageId);

  // Report this image to the bulk-selection registry while it is mounted, so
  // "select all"/the selected count only ever cover images that are actually
  // rendered as selectable. Outside a selection provider both calls are no-ops.
  useEffect(
    function registerSelectableAuditImage() {
      if (isNil(imageId)) {
        return;
      }
      registerSelectableImage(imageId);
      return () => unregisterSelectableImage(imageId);
    },
    [imageId, registerSelectableImage, unregisterSelectableImage]
  );

  const handleClick = () => {
    if (isSelectable && isNotNil(imageId)) {
      toggleImageSelection(imageId);
      return;
    }
    props.onOpenPreview();
  };

  return (
    <Box
      position="relative"
      borderRadius="small"
      overflow="hidden"
      backgroundColor="palettes.neutral.30.100"
      ratio={props.ratio}
    >
      <Clickable
        onClick={handleClick}
        onMouseEnter={handleHover}
        onMouseLeave={handleLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onContextMenu={(event) => {
          event.preventDefault();
          if (!isSelectable && !props.isDisabled && isNotNil(props.contextMenuContent)) {
            openDropdown(event, props.contextMenuContent);
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleClick();
          }
        }}
        data-testid={suffixTestId('image', props)}
      >
        <Show when={props.isImageRotationInProgress}>
          <Box position="absolute" top={0} left={0} zIndex="ROTATE_OVERLAY">
            <Tooltip label={i18n.t('entity.photo.labels.image.processing')}>
              <Icon value="action/watch_later" color="severity.informational" />
            </Tooltip>
          </Box>
        </Show>
        <Show when={isLocalPreview}>
          <Box opacity={props.isLoading ? 0.5 : 1}>
            <Image
              src={props.url}
              ratio={props.ratio}
              fit="cover"
              data-testid={suffixTestId('localPreviewImage', props)}
            />
          </Box>
        </Show>
        <Hide when={isLocalPreview}>
          <AdaptiveImage
            url={props.url}
            makeUrl={makeStorageUrl}
            ratio={props.ratio}
            fit="cover"
            data-testid={suffixTestId('previewImage', props)}
          />
        </Hide>
        <Show when={props.isLoading}>
          <Box position="absolute" top={0} left={0} width="100%" height="100%">
            <Center width="100%" height="100%" justify="center">
              <Spinner />
            </Center>
          </Box>
        </Show>
        <Box
          opacity={isHovered || isFocused ? 1 : 0}
          transition="opacity 0.2s"
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          backgroundColor="palettes.black.900.20"
          borderRadius="small"
          tabIndex={0}
        />
        <Show when={isSelected}>
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            border="2px solid"
            borderColor="palettes.blue.60.100"
            borderRadius="small"
            pointerEvents="none"
          />
        </Show>
        <Show when={isSelectable && !props.isLoading && !props.isDisabled}>
          <Box position="absolute" bottom={0} right={0} padding={2}>
            <Checkbox value={isSelected} data-testid={suffixTestId('imageSelect', props)} />
          </Box>
        </Show>
      </Clickable>
    </Box>
  );
}
